import { MAX_PAGE_SIZE } from '@hvac/core';
import { z } from 'zod';
/** Shared field-level schemas. Defined once so validation cannot drift. */
export const uuidSchema = z.uuid();
// offset: true, learned from the first real invoice. Postgres hands
// timestamps to the API as "+00:00", not "Z", and a validator that only
// accepts Z rejects every raw database timestamp that reaches the browser.
export const isoDateTimeSchema = z.iso.datetime({ offset: true });
export const isoDateSchema = z.iso.date();
/** Canadian provinces and territories. The organisation's fiscal jurisdiction. */
export const CANADIAN_PROVINCES = [
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NS',
    'NT',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT',
];
export const provinceSchema = z.enum(CANADIAN_PROVINCES);
export const currencySchema = z.enum(['CAD', 'USD']);
/** New Brunswick is officially bilingual; the UI must support both from day one. */
export const localeSchema = z.enum(['en-CA', 'fr-CA']);
/** Standard keyset pagination query. */
export const pageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(25),
    cursor: z.string().min(1).optional(),
});
/** Wraps an item schema in the paginated payload shape. */
export function pageSchema(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
        hasMore: z.boolean(),
    });
}
/** One hit from the search spine. The index finds; the entity is consulted. */
export const searchHitSchema = z.object({
    entityType: z.enum(['document', 'transaction', 'vendor', 'customer', 'job', 'equipment', 'note']),
    entityId: uuidSchema,
    title: z.string().nullable(),
    snippet: z.string().nullable(),
    occurredOn: z.string().nullable(),
    amountMinor: z.string().nullable(),
});
/**
 * One edge bundle in an object's neighbourhood: what surrounds it, how much
 * of it, and how recently. A read of the graph the foreign keys already form.
 */
export const objectRelationSchema = z.object({
    relation: z.string(),
    entityType: z.string(),
    itemCount: z.number().int().nonnegative(),
    totalMinor: z.string().nullable(),
    latestOn: z.string().nullable(),
});
/**
 * One deterministic fact about an object: what it is, the amount behind it,
 * how many confirmed observations back it, and how recent the latest one is.
 * Prose is composed FROM these rows; nothing composes rows from prose.
 */
export const objectFactSchema = z.object({
    fact: z.string(),
    amountMinor: z.string().nullable(),
    observationCount: z.number().int().nonnegative(),
    asOf: z.string().nullable(),
});
/** One month of the year's rhythm: earned, spent, and booked. Minor units. */
export const monthlyFlowSchema = z.object({
    month: z.string(),
    earnedMinor: z.string(),
    spentMinor: z.string(),
    bookedMinor: z.string(),
});
/** One ranked row of a where-does-it-go breakdown. Minor units. */
export const reportRankRowSchema = z.object({
    label: z.string(),
    sublabel: z.string().nullable(),
    totalMinor: z.string(),
    count: z.number().int(),
    /** Present when the row is a supplier and has a page to open. */
    vendorId: uuidSchema.nullable(),
});
/**
 * One account on the profit and loss statement. Signed and unsigned are kept
 * apart: a record nobody has signed is shown, never counted as certain.
 * `outside` holds capital, inventory and owner lines, which reconcile what was
 * spent to the P&L without belonging on it.
 */
export const profitAndLossRowSchema = z.object({
    section: z.enum(['revenue', 'cost_of_goods_sold', 'expense', 'outside']),
    accountId: uuidSchema,
    accountCode: z.string(),
    accountName: z.string(),
    signedMinor: z.string(),
    unsignedMinor: z.string(),
    lineCount: z.number().int(),
    /** Lines whose account and tax treatment disagree. Placed by account, flagged here. */
    disagreements: z.number().int(),
});
export const profitAndLossQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    divisionId: uuidSchema.optional(),
});
/** One account's balance at a date, from the ledger. Debits total credits. */
export const trialBalanceRowSchema = z.object({
    accountId: uuidSchema,
    accountCode: z.string().nullable(),
    accountName: z.string(),
    accountType: z.string(),
    debitMinor: z.string(),
    creditMinor: z.string(),
});
/** A balance sheet line. Earnings to date has no account: it is computed. */
export const balanceSheetRowSchema = z.object({
    section: z.enum(['asset', 'liability', 'equity']),
    accountId: uuidSchema.nullable(),
    accountCode: z.string().nullable(),
    accountName: z.string(),
    amountMinor: z.string(),
});
/** One bank or card account: statement, books, what is waiting, the difference. */
export const bankReconciliationRowSchema = z.object({
    financialAccountId: uuidSchema,
    accountName: z.string(),
    accountKind: z.string(),
    statementMinor: z.string(),
    booksMinor: z.string(),
    waitingMinor: z.string(),
    waitingCount: z.number().int(),
    differenceMinor: z.string(),
    hasOpeningBalance: z.boolean(),
    /** The opening balance the statement's own running balance implies, where it gives one. */
    statementOpeningMinor: z.string().nullable(),
    openingAgrees: z.boolean().nullable(),
});
/**
 * One unpaid invoice. `owedMinor` comes from Housecall's outstanding balance
 * when it reported one (`source: 'housecall'`), otherwise from the invoice
 * less the deposits matched to it (`source: 'deposits'`).
 */
export const receivableRowSchema = z.object({
    transactionId: uuidSchema,
    status: z.enum(['draft', 'confirmed']),
    customerId: uuidSchema.nullable(),
    customerName: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    jobId: uuidSchema.nullable(),
    issuedOn: isoDateSchema,
    daysOutstanding: z.number().int(),
    bucket: z.enum(['current', '31-60', '61-90', '90+']),
    invoicedMinor: z.string(),
    receivedMinor: z.string(),
    externalOutstandingMinor: z.string().nullable(),
    externalOutstandingAt: z.string().nullable(),
    owedMinor: z.string(),
    source: z.enum(['housecall', 'deposits']),
});
/** A supplier bill not yet paid by a matched bank line at the as-of date. */
export const payableRowSchema = z.object({
    transactionId: uuidSchema,
    status: z.enum(['draft', 'confirmed']),
    vendorId: uuidSchema.nullable(),
    vendorName: z.string().nullable(),
    reference: z.string().nullable(),
    documentId: uuidSchema.nullable(),
    issuedOn: isoDateSchema,
    dueOn: isoDateSchema.nullable(),
    dueOnSource: z.enum(['document', 'terms', 'assumed', 'person']).nullable(),
    daysOutstanding: z.number().int(),
    /** Positive when past due, negative when still inside its terms, null without a due date. */
    daysOverdue: z.number().int().nullable(),
    bucket: z.enum(['current', '31-60', '61-90', '90+']),
    billedMinor: z.string(),
    paidMinor: z.string(),
    owedMinor: z.string(),
});
export const asOfQuerySchema = z.object({ asOf: isoDateSchema });
const gifiCodeSchema = z.string().regex(/^\d{4}$/, 'A GIFI code is four digits.');
const minorSchema = z.string().regex(/^\d{1,15}$/);
export const chartAccountSchema = z.object({
    id: uuidSchema,
    code: z.string().nullable(),
    name: z.string(),
    accountType: z.string(),
    gifiCode: z.string().nullable(),
    gifiCodeIfReversed: z.string().nullable(),
});
export const setAccountGifiRequestSchema = z.object({
    gifiCode: gifiCodeSchema,
    gifiCodeIfReversed: gifiCodeSchema.nullable().optional(),
});
export const openingBalanceLineSchema = z.object({
    accountId: uuidSchema,
    debitMinor: minorSchema,
    creditMinor: minorSchema,
});
export const openingBalancesSchema = z.object({
    asOf: isoDateSchema.nullable(),
    lines: z.array(openingBalanceLineSchema.extend({
        accountCode: z.string().nullable(),
        accountName: z.string(),
        accountType: z.string(),
        gifiCode: z.string().nullable(),
    })),
});
export const setOpeningBalancesRequestSchema = z.object({
    asOf: isoDateSchema,
    lines: z.array(openingBalanceLineSchema).max(500),
    memo: z.string().max(500).optional(),
});
/** One line of an accountant's adjusting entry. */
export const adjustmentLineSchema = z.object({
    accountId: uuidSchema,
    debitMinor: minorSchema,
    creditMinor: minorSchema,
    description: z.string().trim().max(200).nullable().optional(),
});
export const recordAdjustmentRequestSchema = z.object({
    entryDate: isoDateSchema,
    /** Why. Required: it is what the Timeline shows. */
    memo: z.string().trim().min(3).max(500),
    lines: z.array(adjustmentLineSchema).min(2).max(100),
});
export const adjustmentSchema = z.object({
    entryId: uuidSchema,
    entryDate: isoDateSchema,
    memo: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    createdByName: z.string().nullable(),
    reversedByEntryId: uuidSchema.nullable(),
    reversedOn: isoDateSchema.nullable(),
    reversalMemo: z.string().nullable(),
    lines: z.array(z.object({
        accountId: uuidSchema,
        accountCode: z.string().nullable(),
        accountName: z.string(),
        debitMinor: z.string(),
        creditMinor: z.string(),
        description: z.string().nullable(),
    })),
});
export const reverseAdjustmentRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/** One client's month, for the firm's desk. */
export const clientSummarySchema = z.object({
    documentsWaiting: z.number().int(),
    oldestWaiting: isoDateSchema.nullable(),
    drafts: z.number().int(),
    readyToSign: z.number().int(),
    lastStatementOn: isoDateSchema.nullable(),
    unmatchedBankLines: z.number().int(),
    lastClosedLabel: z.string().nullable(),
    lastClosedEnd: isoDateSchema.nullable(),
    hstPeriodLabel: z.string().nullable(),
    hstPeriodEnd: isoDateSchema.nullable(),
    hstFilingDueOn: isoDateSchema.nullable(),
    hstStatus: z.string().nullable(),
    billsOverdue: z.number().int(),
    billsOverdueMinor: z.string(),
    owedToUsMinor: z.string(),
});
export const openingFromGifiRequestSchema = z.object({
    items: z
        .array(z.object({ gifi: gifiCodeSchema, amountMinor: z.string().regex(/^-?\d{1,15}$/) }))
        .min(1)
        .max(500),
});
export const openingFromGifiRowSchema = z.object({
    gifiCode: z.string(),
    amountMinor: z.string(),
    accountId: uuidSchema.nullable(),
    accountCode: z.string().nullable(),
    accountName: z.string().nullable(),
    debitMinor: z.string(),
    creditMinor: z.string(),
    skippedReason: z.string().nullable(),
});
export const gifiRowSchema = z.object({
    schedule: z.enum(['100', '125']),
    gifiCode: z.string().nullable(),
    amountMinor: z.string(),
    accounts: z.string().nullable(),
    isTotal: z.boolean(),
});
/**
 * A structured query — what the deterministic parser produces and run_query
 * executes. Stored verbatim inside a collection: structure re-runs forever,
 * natural language drifts.
 */
export const queryFiltersSchema = z.object({
    words: z.array(z.string().min(1).max(40)).max(8).optional(),
    entityTypes: z
        .array(z.enum(['document', 'transaction', 'vendor', 'customer', 'job', 'equipment']))
        .max(6)
        .optional(),
    amountMinMinor: z.string().regex(/^\d+$/).optional(),
    amountMaxMinor: z.string().regex(/^\d+$/).optional(),
    amountAroundMinor: z.string().regex(/^\d+$/).optional(),
    dateFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    dateTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
/** A collection: a named, saved query. */
export const savedViewSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    filters: queryFiltersSchema,
    createdAt: isoDateTimeSchema,
});
/** An integration as a person may see it: status and the key's last four, never the key. */
export const integrationConnectionSchema = z.object({
    provider: z.enum(['housecall_pro']),
    status: z.enum(['connected', 'failing', 'disconnected']),
    secretHint: z.string().nullable(),
    connectedAt: z.string(),
    lastSyncedAt: z.string().nullable(),
    lastError: z.string().nullable(),
});
export const connectIntegrationRequestSchema = z.object({
    apiKey: z.string().trim().min(10).max(500),
});
/** A pending request to delete the business, as every member sees it. */
export const organizationDeletionSchema = z.object({
    requestedAt: z.string(),
    purgeAfter: z.string(),
    requestedByName: z.string().nullable(),
    reason: z.string().nullable(),
});
export const requestDeletionSchema = z.object({
    /** The business's name, typed: a button is too easy to press. */
    confirmName: z.string().trim().min(1).max(200),
    /** The owner's own account password, re-entered: proves it is them, not an unlocked laptop. */
    password: z.string().min(1).max(200),
    reason: z.string().trim().max(1000).optional(),
});
/** A full export of the business's data. Parts are zips, downloaded through short-lived links. */
export const dataExportSchema = z.object({
    id: uuidSchema,
    status: z.enum(['queued', 'building', 'ready', 'failed', 'expired']),
    parts: z.array(z.object({ name: z.string(), bytes: z.number().int() })),
    error: z.string().nullable(),
    /** Everything the business owns, or one year's working papers. */
    kind: z.enum(['everything', 'year_end']),
    fiscalYear: z.number().int().nullable(),
    createdAt: z.string(),
    expiresAt: z.string().nullable(),
});
export const requestDataExportSchema = z.object({
    kind: z.enum(['everything', 'year_end']).default('everything'),
    fiscalYear: z.number().int().min(2000).max(2100).optional(),
});
//# sourceMappingURL=common.js.map